// corpusMode — whether the spine's base is the remote corpus or plain localStorage (PB-04 Inc 2).
//
// READ ONCE, AT MODULE LOAD. `spineStore.ts` creates its stores as module singletons, so the
// backing cannot change under a running app without leaving components subscribed to a store that
// no longer exists. Toggling therefore RELOADS — the same thing "Reset local data" already does,
// and honest about what is happening rather than half-swapping the spine beneath the UI.
//
// Charter Abs-7: switching modes DESTROYS NOTHING. Corpus mode overlays at `<key>:overlay` and
// leaves `<key>` untouched, so turning it off restores the previous local spine byte-for-byte.

const FLAG_KEY = "oxbo-corpus-mode-v1";

// DEFAULT ON — the database is the product's only data path (PO, 2026-09-06).
//
// It shipped OFF for months and that was the bug, not the caution. With it off `useDemoSeed` wrote
// a whole generated oilfield into localStorage — spine, AFE seed, and a time-log shard per job —
// and an unrelated AFE save then died of QuotaExceededError with nothing to tell the user why. The
// spine's own header had already named that starvation as BACKLOG PB-01. Corpus mode was built to
// fix it and was never turned on.
//
// The two paths were never equal, either: the daily-cost strip runs only when this is off and the
// fetch only when it is on, mutually exclusive, so local mode showed a blank "Cost to Date" KPI
// and would have needed the Python writers reimplemented on the front end to ever catch up —
// which Charter Abs-2 forbids.
//
// The PO's ruling, asked as a question and answered as one: "is there any reason to have local
// data at all at this point? Are we just making future pain by not fully switching?" followed by
// "so make the database the default."
//
// `"off"` still opts out, so nothing is destroyed and a developer with no compute server running
// has a way back. Retiring that escape hatch entirely is the next step, not this one.
function readFlag(): boolean {
  try {
    return localStorage.getItem(FLAG_KEY) !== "off";
  } catch {
    return true; // storage blocked → still the database; it needs no storage to read
  }
}

/** Snapshot taken at module load. Deliberately NOT re-read: see the header. */
export const CORPUS_MODE = readFlag();

export function isCorpusMode(): boolean {
  return CORPUS_MODE;
}

/** Persist the choice and reload so the spine is rebuilt against the new backing. */
export function setCorpusMode(on: boolean): void {
  try {
    // The flag now records DISSENT from the default rather than consent to it.
    if (on) localStorage.removeItem(FLAG_KEY);
    else localStorage.setItem(FLAG_KEY, "off");
  } catch {
    /* storage blocked — the reload below will simply land back in local mode */
  }
  window.location.reload();
}

export { FLAG_KEY as CORPUS_MODE_KEY };
